import { ApiUTMEntityLevel } from "@incendium/api";
import {
  Autocomplete,
  Box,
  TextField,
  Typography,
} from "@mui/material";
import { useMetaUtmEntites } from "features/dataConnectors/hooks/useMetaUtmEntites";
import { buildFlatEntityList } from "features/rezonate/utils/buildEntityHierarchy";
import { useMemo } from "react";
import EntityTypeChip from "./EntityTypeChip";
import { EntityType } from "../types";

interface AdOption {
  id: number;
  name: string;
  level: ApiUTMEntityLevel;
  type: "ad";
  campaignName?: string;
  adGroupName?: string;
  campaignId?: number;
  adGroupId?: number;
}

interface AdSelectorProps {
  value: AdOption | null;
  onChange: (ad: AdOption | null) => void;
  label?: string;
  disabled?: boolean;
  required?: boolean;
}

function AdSelector({
  value,
  onChange,
  label = "Select Ad",
  disabled = false,
  required = false,
}: AdSelectorProps) {
  const { entites, loading } = useMetaUtmEntites();

  // Filter to only ads
  const adOptions = useMemo(() => {
    const flatList = buildFlatEntityList(entites);
    return flatList
      .filter((entity) => entity.type === "ad")
      .map((entity) => ({
        id: entity.id,
        name: entity.name,
        level: entity.level,
        type: "ad" as const,
        campaignName: entity.campaignName,
        adGroupName: entity.adGroupName,
        campaignId: entity.campaignId,
        adGroupId: entity.adGroupId,
      })) as AdOption[];
  }, [entites]);

  return (
    <Autocomplete
      options={adOptions}
      getOptionLabel={(option) => option.name}
      value={value}
      onChange={(_, newValue) => onChange(newValue)}
      loading={loading}
      disabled={disabled}
      size="medium"
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          placeholder="Select an ad"
        />
      )}
      renderOption={(props, option) => (
        <Box
          component="li"
          {...props}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <EntityTypeChip type={EntityType.AD} />
          <Typography variant="body2">{option.name}</Typography>
          {option.campaignName && (
            <Typography variant="caption" color="text.secondary">
              ({option.adGroupName} • {option.campaignName})
            </Typography>
          )}
        </Box>
      )}
      isOptionEqualToValue={(option, value) => option.id === value.id}
    />
  );
}

export default AdSelector;
export type { AdOption };

